import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Volume2, VolumeX, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface TextToSpeechProps {
  text: string;
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
  /** BCP-47 language code (e.g. "fr-FR", "en-US"). Defaults to "fr-FR". */
  lang?: string;
  className?: string;
}

/**
 * Uses the browser's native Web Speech API (SpeechSynthesis).
 * - Free, no server call, works offline.
 * - Defaults to French because briefings are written in French.
 * - The `voice` prop is kept for backward compatibility but is ignored.
 */
export const TextToSpeech = ({ text, lang = "fr-FR", className = "" }: TextToSpeechProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [supported, setSupported] = useState(true);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    setSupported(typeof window !== "undefined" && "speechSynthesis" in window);
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const pickVoice = (targetLang: string): SpeechSynthesisVoice | null => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;

    // Only true France voices (fr-FR), exclude fr-CA / fr-BE / fr-CH accents.
    const frVoices = voices.filter(
      (v) => v.lang?.toLowerCase() === "fr-fr"
    );
    if (!frVoices.length) return null;

    // Prefer warmer, well-known high-quality French voices across platforms.
    const preferredNames = [
      "amélie",
      "amelie",
      "audrey",
      "eloise",
      "éloise",
      "hortense",
      "julie",
      "thomas",
      "google français",
      "google french",
    ];
    for (const name of preferredNames) {
      const match = frVoices.find(
        (v) => v.name.toLowerCase().includes(name)
      );
      if (match) return match;
    }

    // Fallback to the first available fr-FR voice.
    return frVoices[0];
  };

  const handlePlayPause = () => {
    if (!supported) {
      toast({
        title: "Lecture audio indisponible",
        description: "Votre navigateur ne supporte pas la synthèse vocale.",
        variant: "destructive",
      });
      return;
    }

    const synth = window.speechSynthesis;

    // Toggle pause/resume if we're already playing
    if (isPlaying || synth.speaking) {
      synth.cancel();
      setIsPlaying(false);
      setIsLoading(false);
      return;
    }

    if (!text?.trim()) return;

    // CRITICAL: create the utterance synchronously inside the user gesture
    // so browsers (notably Safari/iOS/Chrome) allow speech to start.
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    // Slightly slower and lower pitch for a warmer, more pleasant feel.
    u.rate = 0.92;
    u.pitch = 0.95;
    u.onstart = () => {
      setIsLoading(false);
      setIsPlaying(true);
    };
    u.onend = () => setIsPlaying(false);
    u.onerror = (e) => {
      setIsLoading(false);
      setIsPlaying(false);
      const err = (e as SpeechSynthesisErrorEvent).error;
      if (err !== "canceled" && err !== "interrupted") {
        toast({
          title: "Erreur de lecture",
          description: "Impossible de lire le texte.",
          variant: "destructive",
        });
      }
    };
    utteranceRef.current = u;

    // Assign a matching voice only if voices are already loaded.
    // Waiting for `voiceschanged` breaks the user-gesture context and
    // prevents playback from ever starting.
    const voices = synth.getVoices();
    if (voices.length) {
      const v = pickVoice(lang);
      if (v) u.voice = v;
    }

    setIsLoading(true);
    synth.cancel();
    synth.speak(u);
  };

  return (
    <Button
      onClick={handlePlayPause}
      disabled={isLoading || !text}
      variant="outline"
      size="icon"
      className={className}
      title={isPlaying ? "Arrêter" : "Écouter"}
      aria-label={isPlaying ? "Arrêter la lecture" : "Écouter le texte"}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isPlaying ? (
        <VolumeX className="w-4 h-4" />
      ) : (
        <Volume2 className="w-4 h-4" />
      )}
    </Button>
  );
};
